import { PlusOutlined } from '@ant-design/icons';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useDrop } from 'react-dnd';
import { FloatingWindow, Product } from 'types/app.types';
import { MapViewProvider } from 'contexts/MapViewContext';
import TileViewContext from 'contexts/TileViewContext';
import SingleTileViewWindow from '../SingleTileViewWindow';
import { FloatingWindowsProvider } from '../../../../../../../../../../../contexts/FloatingWindowsContext';

interface SingleTileViewProps {
    product: Product,
    className: string,
}

function SingleTileView({ product, className } : SingleTileViewProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const { setMousePosition, mousePosition, syncLayers } = useContext(TileViewContext);
  const [isAboveTheMap, setIsAboveTheMap] = useState(true);
  const [, drop] = useDrop(() => ({
    accept: `TILE_WINDOW_${product.index}`,
    drop(item: FloatingWindow, monitor) {
      const delta = monitor.getDifferenceFromInitialOffset() as {
                x: number
                y: number
              };
      return { top: delta.y, left: delta.x };
    },
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const listener = (e : MouseEvent) => {
      const currentTarget = e.currentTarget as HTMLDivElement;
      const target = e.target as HTMLDivElement;
      const rect = currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left - 8; // -8 is the width/2 of the cursor
      const y = e.clientY - rect.top - 8; // -8 is the height/2 of the cursor
      if (syncLayers) {
        setMousePosition([x, y]);
        setIsAboveTheMap(target.tagName === 'CANVAS' || target.className === 'singleTileWrapper');
      }
    };
    canvas?.addEventListener('mousemove', listener, false);
    return () => {
      canvas?.removeEventListener('mousemove', listener, false);
    };
  }, [product.index, canvasRef, setMousePosition, syncLayers]);

  return (
    <div ref={canvasRef} className="singleTileWrapper">
      <div key={product.id} ref={drop} style={{ width: '100%', height: '100%' }}>
        <FloatingWindowsProvider>
          <MapViewProvider index={product.index}>
            <SingleTileViewWindow className={`tiledView ${className}`} index={product.index} />
            {(syncLayers && isAboveTheMap) && (
            <PlusOutlined
              ref={cursorRef}
              style={{
                position: 'absolute', left: mousePosition[0], top: mousePosition[1], zIndex: 1, pointerEvents: 'none',
              }}
            />
            )}
          </MapViewProvider>
        </FloatingWindowsProvider>
      </div>
    </div>
  );
}

export default SingleTileView;
